import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { useIsMobile } from '../../utils/useIsMobile';

const PESTANAS = [
  { key: 'recibidos',  label: '💵 Pagos recibidos' },
  { key: 'gestion',    label: '⏳ Pendientes, fallidos y reembolsos' },
  { key: 'config',     label: '⚙️ Métodos, conciliación y configuración' },
];

// Lista fija de metodos: no debe depender de la base de datos, porque si las
// tablas de pagos aun no existen el desplegable se quedaba vacio.
const METODOS = [
  'Transferencia', 'PSE', 'Tarjeta de crédito', 'Tarjeta débito',
  'Mercado Pago', 'PayPal', 'Nequi', 'Daviplata', 'Efectivo', 'Otro',
];

const COLOR_ESTADO = { recibido: '#16a34a', pendiente: '#f59e0b', fallido: '#ef4444' };
const LABEL_ESTADO = { recibido: 'Recibido', pendiente: 'Pendiente', fallido: 'Fallido' };

const fmtDinero = v => '$ ' + Number(v || 0).toLocaleString('es-CO');
const fmtNum    = v => Number(v || 0).toLocaleString('es-CO');
const fmtFecha  = f => {
  if (!f) return '—';
  const [y, m, d] = String(f).split('-');
  return d ? `${d}/${m}/${y}` : String(f);
};
const hoy = () => new Date().toISOString().split('T')[0];
const primerDiaMes = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0]; };

export default function PaymentsPage() {
  const esMovil = useIsMobile();
  const [pestana, setPestana] = useState('recibidos');
  const [resumen, setResumen] = useState(null);
  const [negocios, setNegocios] = useState([]);
  const [planes, setPlanes]     = useState([]);
  const [ok, setOk]       = useState('');
  const [error, setError] = useState('');

  const avisar = (msg, esError = false) => {
    if (esError) { setError(msg); setTimeout(() => setError(''), 6000); }
    else          { setOk(msg);   setTimeout(() => setOk(''), 5000); }
  };

  const cargarResumen = useCallback(async () => {
    try {
      const r = await api.get('/payments/summary');
      setResumen(r.data);
    } catch (err) { avisar(err.response?.data?.error || 'Error cargando el resumen de pagos', true); }
  }, []);

  useEffect(() => {
    cargarResumen();
    api.get('/businesses').then(r => setNegocios(r.data)).catch(() => {});
    api.get('/plans').then(r => setPlanes(r.data)).catch(() => {});
  }, [cargarResumen]);

  const tarjeta = { background: 'var(--color-surface-raised, #fff)', border: '1px solid var(--color-border)', borderRadius: 14, padding: '1.1rem 1.25rem' };

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="page-header-text">
          <h2>Pagos</h2>
          <p>Resumen general de los pagos de la plataforma</p>
        </div>
      </div>

      {ok    && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{ok}</div>}
      {error && <div className="alert alert-danger"  style={{ marginBottom: '1rem' }}>{error}</div>}

      {/* Indicadores generales */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: esMovil ? '1fr 1fr' : 'repeat(auto-fit, minmax(210px, 1fr))',
        gap: '1rem', marginBottom: '1.25rem',
      }}>
        {[
          { label: 'Total recibido', valor: fmtDinero(resumen?.total_recibido), sub: `${fmtNum(resumen?.n_recibidos)} pagos`, color: '#16a34a',
            variacion: resumen?.variacion_mes },
          { label: 'Pagos pendientes', valor: fmtDinero(resumen?.total_pendiente), sub: `${fmtNum(resumen?.n_pendientes)} pendientes`, color: '#f59e0b' },
          { label: 'Pagos fallidos',   valor: fmtDinero(resumen?.total_fallido),   sub: `${fmtNum(resumen?.n_fallidos)} fallidos`,   color: '#ef4444' },
          { label: 'Reembolsos',       valor: fmtDinero(resumen?.total_reembolsos), sub: `${fmtNum(resumen?.n_reembolsos)} reembolsos`, color: '#8b5cf6' },
        ].map(k => (
          <div key={k.label} style={tarjeta}>
            <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 7 }}>{k.label}</div>
            <div style={{ fontSize: esMovil ? '1.1rem' : '1.4rem', fontWeight: 800, color: k.color }}>{k.valor}</div>
            <div style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)', marginTop: 3 }}>{k.sub}</div>
            {k.variacion !== undefined && k.variacion !== null && (
              <div style={{ fontSize: '0.74rem', marginTop: 2, color: k.variacion >= 0 ? '#16a34a' : '#ef4444' }}>
                {k.variacion >= 0 ? '↑' : '↓'} {Math.abs(k.variacion)}% vs mes anterior
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pestañas */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: '1.25rem' }}>
        {PESTANAS.map(p => {
          const activa = pestana === p.key;
          return (
            <button key={p.key} onClick={() => setPestana(p.key)} style={{
              padding: '9px 16px', borderRadius: 10, cursor: 'pointer', fontSize: '0.85rem',
              fontWeight: activa ? 700 : 500, fontFamily: 'var(--font-body)',
              border: `1px solid ${activa ? 'var(--color-gold)' : 'var(--color-border)'}`,
              background: activa ? 'rgba(79,70,229,0.10)' : 'transparent',
              color: activa ? 'var(--color-gold)' : 'var(--color-text-muted)',
            }}>{p.label}</button>
          );
        })}
      </div>

      {pestana === 'recibidos' && (
        <PagosRecibidos negocios={negocios} planes={planes} esMovil={esMovil}
          tarjeta={tarjeta} avisar={avisar} onCambio={cargarResumen} resumen={resumen} />
      )}
      {pestana === 'gestion' && (
        <Gestion negocios={negocios} esMovil={esMovil} tarjeta={tarjeta}
          avisar={avisar} onCambio={cargarResumen} />
      )}
      {pestana === 'config' && (
        <MetodosYConfig esMovil={esMovil} tarjeta={tarjeta} avisar={avisar} />
      )}
    </div>
  );
}

/* ══════════════════════════════════ PESTAÑA 1: PAGOS RECIBIDOS ══════════ */
function PagosRecibidos({ negocios, planes, esMovil, tarjeta, avisar, onCambio, resumen }) {
  const [lista, setLista]   = useState([]);
  const [filtros, setFiltros] = useState({ desde: '', hasta: '', metodo: '', plan_id: '', search: '' });
  const [pagina, setPagina] = useState(1);
  const [paginas, setPaginas] = useState(1);
  const [total, setTotal]   = useState(0);
  const [cargando, setCargando] = useState(true);
  const [modal, setModal]   = useState(false);
  // Arranca con la lista fija; si el servidor devuelve otra, la reemplaza
  const [metodos, setMetodos] = useState(METODOS);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const params = { estado: 'recibido', pagina, por_pagina: 10 };
      Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get('/payments', { params });
      setLista(r.data.pagos || []);
      setPaginas(r.data.paginas || 1);
      setTotal(r.data.total || 0);
    } catch (err) { avisar(err.response?.data?.error || 'Error cargando pagos', true); }
    finally { setCargando(false); }
  }, [filtros, pagina, avisar]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    api.get('/payments/methods')
      .then(r => { if (r.data?.disponibles?.length) setMetodos(r.data.disponibles); })
      .catch(() => { /* se conserva la lista fija */ });
  }, []);

  const exportar = () => {
    const cab = ['ID', 'Negocio', 'Plan', 'Fecha', 'Método', 'Monto', 'Referencia'];
    const filas = lista.map(p => [`PAY-${String(p.id).padStart(5, '0')}`, p.negocio || '', p.plan || '',
      fmtFecha(p.fecha_pago), p.metodo || '', p.monto, p.referencia || '']);
    const csv = [cab, ...filas].map(f => f.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(';')).join('\r\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `pagos-recibidos-${hoy()}.csv`;
    a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <>
      {/* Evolución mensual de lo recibido */}
      {(resumen?.serie_mensual || []).length > 0 && (
        <div style={{ ...tarjeta, marginBottom: '1.25rem' }}>
          <div style={{ fontWeight: 700, fontSize: '0.93rem', marginBottom: 12 }}>Pagos recibidos por mes</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height: 120 }}>
            {(() => {
              const max = Math.max(...resumen.serie_mensual.map(s => s.total), 1);
              return resumen.serie_mensual.map(s => (
                <div key={s.mes} style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                  <div style={{ fontSize: '0.68rem', color: 'var(--color-text-muted)', marginBottom: 4 }}>
                    {fmtDinero(s.total)}
                  </div>
                  <div style={{ height: `${Math.max(4, (s.total / max) * 80)}px`, background: '#16a34a', borderRadius: '5px 5px 0 0', opacity: 0.85 }} />
                  <div style={{ fontSize: '0.68rem', color: 'var(--color-text-muted)', marginTop: 4 }}>{s.mes}</div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}

      <div style={{ ...tarjeta, marginBottom: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: esMovil ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 10 }}>
          <input type="date" className="form-input" title="Desde" value={filtros.desde}
            onChange={e => { setFiltros(f => ({ ...f, desde: e.target.value })); setPagina(1); }} />
          <input type="date" className="form-input" title="Hasta" value={filtros.hasta}
            onChange={e => { setFiltros(f => ({ ...f, hasta: e.target.value })); setPagina(1); }} />
          <select className="form-select" value={filtros.metodo}
            onChange={e => { setFiltros(f => ({ ...f, metodo: e.target.value })); setPagina(1); }}>
            <option value="">Todos los métodos</option>
            {metodos.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="form-select" value={filtros.plan_id}
            onChange={e => { setFiltros(f => ({ ...f, plan_id: e.target.value })); setPagina(1); }}>
            <option value="">Todos los planes</option>
            {planes.map(p => <option key={p.id} value={p.id}>{p.nombre_visible}</option>)}
          </select>
          <input className="form-input" placeholder="Buscar negocio o referencia..." value={filtros.search}
            onChange={e => { setFiltros(f => ({ ...f, search: e.target.value })); setPagina(1); }} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" style={{ fontSize: '0.82rem' }} onClick={exportar}>⬇ Exportar</button>
          <button className="btn btn-primary" style={{ fontSize: '0.82rem' }} onClick={() => setModal(true)}>+ Registrar pago</button>
        </div>
      </div>

      <div className="card">
        {cargando ? (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>
        ) : lista.length === 0 ? (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            No hay pagos recibidos con estos filtros
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>ID Pago</th><th>Negocio</th>{!esMovil && <th>Plan</th>}<th>Fecha</th>
                  {!esMovil && <th>Método</th>}<th>Monto</th>{!esMovil && <th>Referencia</th>}
                </tr>
              </thead>
              <tbody>
                {lista.map(p => (
                  <tr key={p.id}>
                    <td style={{ fontSize: '0.8rem', fontWeight: 600 }}>PAY-{String(p.id).padStart(5, '0')}</td>
                    <td style={{ fontSize: '0.85rem' }}>{p.negocio || '—'}</td>
                    {!esMovil && <td><span className="badge badge-gold" style={{ fontSize: '0.68rem' }}>{p.plan || '—'}</span></td>}
                    <td style={{ fontSize: '0.83rem', whiteSpace: 'nowrap' }}>{fmtFecha(p.fecha_pago)}</td>
                    {!esMovil && <td style={{ fontSize: '0.83rem' }}>{p.metodo || '—'}</td>}
                    <td style={{ fontWeight: 700, color: '#16a34a', whiteSpace: 'nowrap' }}>{fmtDinero(p.monto)}</td>
                    {!esMovil && <td style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>{p.referencia || '—'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Paginacion pagina={pagina} paginas={paginas} total={total} porPagina={10} setPagina={setPagina} etiqueta="pagos" />

      {modal && (
        <ModalPago negocios={negocios} planes={planes} metodos={metodos}
          onCerrar={() => setModal(false)}
          onGuardado={() => { setModal(false); cargar(); onCambio(); avisar('Pago registrado'); }}
          onError={m => avisar(m, true)} />
      )}
    </>
  );
}

/* ══════════════════ PESTAÑA 2: PENDIENTES, FALLIDOS Y REEMBOLSOS ════════ */
function Gestion({ negocios, esMovil, tarjeta, avisar, onCambio }) {
  const [seccion, setSeccion]   = useState('pendientes');
  const [lista, setLista]       = useState([]);
  const [reembolsos, setReemb]  = useState([]);
  const [cargando, setCargando] = useState(true);
  const [modalReemb, setModalReemb] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      if (seccion === 'reembolsos') {
        const r = await api.get('/payments/refunds');
        setReemb(r.data || []);
      } else {
        const r = await api.get('/payments', { params: { estado: seccion === 'pendientes' ? 'pendiente' : 'fallido', por_pagina: 100 } });
        setLista(r.data.pagos || []);
      }
    } catch (err) { avisar(err.response?.data?.error || 'Error cargando', true); }
    finally { setCargando(false); }
  }, [seccion, avisar]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiarEstado = async (pago, estado) => {
    let motivo = null;
    if (estado === 'fallido') {
      motivo = window.prompt('Motivo del fallo:', pago.motivo_fallo || 'Pago rechazado');
      if (motivo === null) return;
    }
    try {
      await api.patch(`/payments/${pago.id}/estado`, { estado, motivo_fallo: motivo });
      avisar(estado === 'recibido' ? 'Pago marcado como recibido' : 'Pago actualizado');
      cargar(); onCambio();
    } catch (err) { avisar(err.response?.data?.error || 'No se pudo actualizar', true); }
  };

  const generarCobros = async () => {
    if (!window.confirm('Se generará un cobro pendiente por cada negocio activo cuya suscripción vence este mes y que aún no tenga uno. ¿Continuar?')) return;
    try {
      const r = await api.post('/payments/generar-cobros');
      avisar(r.data.message);
      cargar(); onCambio();
    } catch (err) { avisar(err.response?.data?.error || 'No se pudieron generar los cobros', true); }
  };

  const diasVencido = f => {
    if (!f) return null;
    const d = Math.floor((new Date(f + 'T00:00:00') - new Date(hoy() + 'T00:00:00')) / 86400000);
    return d;
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '1rem' }}>
        {[['pendientes', 'Pendientes'], ['fallidos', 'Fallidos'], ['reembolsos', 'Reembolsos']].map(([k, l]) => {
          const activa = seccion === k;
          return (
            <button key={k} onClick={() => setSeccion(k)} style={{
              padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem',
              fontWeight: activa ? 700 : 500, fontFamily: 'var(--font-body)',
              border: `1px solid ${activa ? 'var(--color-border-strong, #94a3b8)' : 'var(--color-border)'}`,
              background: activa ? 'var(--color-surface)' : 'transparent',
              color: activa ? 'var(--color-text)' : 'var(--color-text-muted)',
            }}>{l}</button>
          );
        })}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {seccion === 'pendientes' && (
            <button className="btn btn-secondary" style={{ fontSize: '0.82rem' }} onClick={generarCobros}>
              ⚡ Generar cobros del mes
            </button>
          )}
          {seccion === 'reembolsos' && (
            <button className="btn btn-primary" style={{ fontSize: '0.82rem' }} onClick={() => setModalReemb({})}>
              + Registrar reembolso
            </button>
          )}
        </div>
      </div>

      <div className="card">
        {cargando ? (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>
        ) : seccion === 'reembolsos' ? (
          reembolsos.length === 0 ? (
            <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Sin reembolsos registrados</div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead><tr><th>ID</th><th>Negocio</th><th>Fecha</th><th>Monto</th><th>Motivo</th><th>Estado</th></tr></thead>
                <tbody>
                  {reembolsos.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontSize: '0.8rem', fontWeight: 600 }}>REF-{String(r.id).padStart(5, '0')}</td>
                      <td style={{ fontSize: '0.85rem' }}>{r.negocio || '—'}</td>
                      <td style={{ fontSize: '0.83rem', whiteSpace: 'nowrap' }}>{fmtFecha(r.fecha)}</td>
                      <td style={{ fontWeight: 700, color: '#8b5cf6', whiteSpace: 'nowrap' }}>{fmtDinero(r.monto)}</td>
                      <td style={{ fontSize: '0.82rem' }}>{r.motivo || '—'}</td>
                      <td><span className="badge badge-attended" style={{ fontSize: '0.68rem' }}>{r.estado}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : lista.length === 0 ? (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            {seccion === 'pendientes' ? 'No hay pagos pendientes' : 'No hay pagos fallidos'}
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>ID Pago</th><th>Negocio</th>{!esMovil && <th>Plan</th>}
                  <th>{seccion === 'pendientes' ? 'Vencimiento' : 'Fecha'}</th>
                  <th>Monto</th>
                  {seccion === 'fallidos' && <th>Motivo</th>}
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {lista.map(p => {
                  const d = diasVencido(p.fecha_vencimiento);
                  return (
                    <tr key={p.id}>
                      <td style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                        {seccion === 'pendientes' ? 'PEN' : 'FAL'}-{String(p.id).padStart(5, '0')}
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>{p.negocio || '—'}</td>
                      {!esMovil && <td><span className="badge badge-gold" style={{ fontSize: '0.68rem' }}>{p.plan || '—'}</span></td>}
                      <td style={{ fontSize: '0.83rem', whiteSpace: 'nowrap' }}>
                        {fmtFecha(p.fecha_vencimiento || p.fecha_pago)}
                        {seccion === 'pendientes' && d !== null && (
                          <div style={{ fontSize: '0.72rem', fontWeight: 600, color: d < 0 ? '#ef4444' : d <= 3 ? '#f59e0b' : '#16a34a' }}>
                            {d < 0 ? `Vencido hace ${Math.abs(d)} d` : d === 0 ? 'Vence hoy' : `En ${d} días`}
                          </div>
                        )}
                      </td>
                      <td style={{ fontWeight: 700, whiteSpace: 'nowrap', color: COLOR_ESTADO[p.estado] }}>{fmtDinero(p.monto)}</td>
                      {seccion === 'fallidos' && (
                        <td style={{ fontSize: '0.8rem', color: '#ef4444' }}>{p.motivo_fallo || '—'}</td>
                      )}
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <button className="btn btn-sm btn-primary" style={{ fontSize: '0.75rem' }}
                            onClick={() => cambiarEstado(p, 'recibido')}>✓ Recibido</button>
                          {seccion === 'pendientes' ? (
                            <button className="btn btn-sm btn-ghost" style={{ fontSize: '0.75rem' }}
                              onClick={() => cambiarEstado(p, 'fallido')}>✕ Fallido</button>
                          ) : (
                            <button className="btn btn-sm btn-ghost" style={{ fontSize: '0.75rem' }}
                              onClick={() => cambiarEstado(p, 'pendiente')}>↻ Reintentar</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalReemb && (
        <ModalReembolso negocios={negocios} onCerrar={() => setModalReemb(null)}
          onGuardado={() => { setModalReemb(null); cargar(); onCambio(); avisar('Reembolso registrado'); }}
          onError={m => avisar(m, true)} />
      )}
    </>
  );
}

/* ═════════ PESTAÑA 3: MÉTODOS, CONCILIACIÓN Y CONFIGURACIÓN ═════════════ */
function MetodosYConfig({ esMovil, tarjeta, avisar }) {
  const [metodos, setMetodos] = useState(null);
  const [conc, setConc]       = useState(null);
  const [periodo, setPeriodo] = useState({ desde: primerDiaMes(), hasta: hoy() });
  const [extracto, setExtracto] = useState('');
  const [cuenta, setCuenta]   = useState('');
  const [config, setConfig]   = useState(null);
  const [guardando, setGuardando] = useState(false);

  const cargarConc = useCallback(async () => {
    try {
      const r = await api.get('/payments/reconciliation', { params: periodo });
      setConc(r.data);
      setExtracto(r.data.extracto ?? '');
      setCuenta(r.data.cuenta || '');
    } catch (err) { avisar(err.response?.data?.error || 'Error en conciliación', true); }
  }, [periodo, avisar]);

  useEffect(() => {
    api.get('/payments/methods').then(r => setMetodos(r.data)).catch(() => {});
    api.get('/payments/config').then(r => setConfig(r.data)).catch(() => {});
  }, []);
  useEffect(() => { cargarConc(); }, [cargarConc]);

  const guardarConc = async () => {
    try {
      await api.post('/payments/reconciliation', { ...periodo, cuenta, monto_extracto: Number(extracto) || 0 });
      avisar('Conciliación guardada');
      cargarConc();
    } catch (err) { avisar(err.response?.data?.error || 'No se pudo guardar', true); }
  };

  const guardarConfig = async () => {
    setGuardando(true);
    try {
      await api.put('/payments/config', config);
      avisar('Configuración guardada');
    } catch (err) { avisar(err.response?.data?.error || 'No se pudo guardar', true); }
    finally { setGuardando(false); }
  };

  const COLORES = ['#6366f1', '#16a34a', '#f59e0b', '#ef4444', '#0ea5e9', '#8b5cf6'];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: esMovil ? '1fr' : '1fr 1fr', gap: '1.25rem', alignItems: 'start' }}>
      {/* Métodos de pago */}
      <div style={{ ...tarjeta, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 4 }}>Métodos de pago</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginBottom: 14 }}>
          Distribución real de los pagos recibidos
        </div>
        {!metodos || metodos.metodos.length === 0 ? (
          <div style={{ color: 'var(--color-text-muted)', fontSize: '0.86rem' }}>
            Aún no hay pagos recibidos registrados
          </div>
        ) : (
          <>
            {metodos.metodos.map((m, i) => (
              <div key={m.metodo} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', marginBottom: 4 }}>
                  <span>{m.metodo} <span style={{ color: 'var(--color-text-muted)' }}>({m.cantidad})</span></span>
                  <strong>{fmtDinero(m.total)} <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>{m.porcentaje}%</span></strong>
                </div>
                <div style={{ height: 7, borderRadius: 99, background: 'var(--color-surface)' }}>
                  <div style={{ width: `${m.porcentaje}%`, height: '100%', borderRadius: 99, background: COLORES[i % COLORES.length] }} />
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--color-border)', paddingTop: 10, marginTop: 6 }}>
              <strong>Total</strong><strong style={{ color: 'var(--color-gold)' }}>{fmtDinero(metodos.total)}</strong>
            </div>
          </>
        )}
      </div>

      {/* Conciliación bancaria */}
      <div style={{ ...tarjeta, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 4 }}>Conciliación bancaria</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginBottom: 14 }}>
          Compara lo registrado en la plataforma contra tu extracto
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginBottom: 3 }}>Desde</div>
            <input type="date" className="form-input" value={periodo.desde}
              onChange={e => setPeriodo(p => ({ ...p, desde: e.target.value }))} />
          </div>
          <div>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginBottom: 3 }}>Hasta</div>
            <input type="date" className="form-input" value={periodo.hasta}
              onChange={e => setPeriodo(p => ({ ...p, hasta: e.target.value }))} />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div style={{ padding: '10px 12px', borderRadius: 9, background: 'var(--color-surface)' }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>En la plataforma</div>
            <div style={{ fontWeight: 800 }}>{fmtDinero(conc?.plataforma?.total)}</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>{fmtNum(conc?.plataforma?.cantidad)} pagos</div>
          </div>
          <div style={{
            padding: '10px 12px', borderRadius: 9,
            background: conc?.diferencia === null || conc?.diferencia === undefined
              ? 'var(--color-surface)'
              : (conc.diferencia === 0 ? 'rgba(22,163,74,0.10)' : 'rgba(239,68,68,0.10)'),
          }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>Diferencia</div>
            <div style={{ fontWeight: 800, color: conc?.diferencia === 0 ? '#16a34a' : conc?.diferencia ? '#ef4444' : 'var(--color-text-muted)' }}>
              {conc?.diferencia === null || conc?.diferencia === undefined ? '—' : fmtDinero(conc.diferencia)}
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
              {conc?.diferencia_pct !== null && conc?.diferencia_pct !== undefined ? `${conc.diferencia_pct}%` : 'Sin extracto'}
            </div>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Cuenta bancaria</label>
          <input className="form-input" placeholder="Ej: Bancolombia ahorros 123..." value={cuenta} onChange={e => setCuenta(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Total según el extracto</label>
          <input type="number" className="form-input" placeholder="0" value={extracto} onChange={e => setExtracto(e.target.value)} />
        </div>
        <button className="btn btn-primary" style={{ width: '100%' }} onClick={guardarConc}>Guardar conciliación</button>
      </div>

      {/* Configuración de pagos */}
      <div style={{ ...tarjeta, gridColumn: esMovil ? 'auto' : '1 / -1', minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 14 }}>Configuración de pagos</div>
        {!config ? (
          <div style={{ color: 'var(--color-text-muted)', fontSize: '0.86rem' }}>Cargando...</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: esMovil ? '1fr' : '1fr 1fr', gap: '1.25rem' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.84rem', marginBottom: 10 }}>General</div>
              <div className="form-group">
                <label className="form-label">Moneda</label>
                <select className="form-select" value={config.moneda} onChange={e => setConfig(c => ({ ...c, moneda: e.target.value }))}>
                  {['COP', 'USD', 'MXN', 'EUR'].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div className="form-group">
                  <label className="form-label">Comisión (%)</label>
                  <input type="number" step="0.01" className="form-input" value={config.comision_plataforma}
                    onChange={e => setConfig(c => ({ ...c, comision_plataforma: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">IVA comisión (%)</label>
                  <input type="number" step="0.01" className="form-input" value={config.iva_comision}
                    onChange={e => setConfig(c => ({ ...c, iva_comision: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Días para reintento</label>
                <input className="form-input" placeholder="1,3,7" value={config.dias_reintento}
                  onChange={e => setConfig(c => ({ ...c, dias_reintento: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Email de notificaciones</label>
                <input type="email" className="form-input" placeholder="pagos@tudominio.com" value={config.email_notificaciones}
                  onChange={e => setConfig(c => ({ ...c, email_notificaciones: e.target.value }))} />
              </div>
            </div>

            <div>
              <div style={{ fontWeight: 600, fontSize: '0.84rem', marginBottom: 10 }}>Procesadores de pago</div>
              {(config.procesadores || []).map((p, i) => (
                <div key={p.nombre} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '9px 0', borderBottom: '1px solid var(--color-border)',
                }}>
                  <span style={{ fontSize: '0.86rem' }}>{p.nombre}</span>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: '0.8rem' }}>
                    <input type="checkbox" checked={!!p.activo}
                      onChange={e => setConfig(c => ({
                        ...c,
                        procesadores: c.procesadores.map((x, j) => j === i ? { ...x, activo: e.target.checked ? 1 : 0 } : x),
                      }))} />
                    <span style={{ color: p.activo ? '#16a34a' : 'var(--color-text-muted)' }}>
                      {p.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </label>
                </div>
              ))}
              <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: 10, lineHeight: 1.5 }}>
                Marcar un procesador aquí lo deja registrado como medio aceptado. La conexión
                real con la pasarela (cobro automático) es un desarrollo aparte.
              </div>
            </div>

            <div style={{ gridColumn: esMovil ? 'auto' : '1 / -1' }}>
              <button className="btn btn-primary" onClick={guardarConfig} disabled={guardando}>
                {guardando ? 'Guardando...' : 'Guardar configuración'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════ AUXILIARES ═══ */
function Paginacion({ pagina, paginas, total, porPagina, setPagina, etiqueta }) {
  if (!total) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between', marginTop: '1rem', fontSize: '0.84rem', color: 'var(--color-text-muted)' }}>
      <span>Mostrando {(pagina - 1) * porPagina + 1} a {Math.min(pagina * porPagina, total)} de {fmtNum(total)} {etiqueta}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button className="btn btn-ghost" style={{ padding: '4px 10px' }} disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}>‹</button>
        <span>Página <strong>{pagina}</strong> de {paginas}</span>
        <button className="btn btn-ghost" style={{ padding: '4px 10px' }} disabled={pagina >= paginas} onClick={() => setPagina(p => p + 1)}>›</button>
      </div>
    </div>
  );
}

function ModalPago({ negocios, planes, metodos, onCerrar, onGuardado, onError }) {
  const [f, setF] = useState({
    negocio_id: '', plan_id: '', monto: '', metodo: 'Transferencia',
    estado: 'recibido', referencia: '', fecha_pago: hoy(), fecha_vencimiento: '', notas: '',
  });
  const [guardando, setGuardando] = useState(false);

  // Al elegir el negocio se propone su plan y su precio
  const elegirNegocio = id => {
    const n = negocios.find(x => String(x.id) === String(id));
    const plan = planes.find(p => String(p.id) === String(n?.plan_id));
    setF(v => ({ ...v, negocio_id: id, plan_id: n?.plan_id || '', monto: plan?.precio ?? v.monto }));
  };

  const guardar = async e => {
    e.preventDefault();
    setGuardando(true);
    try {
      await api.post('/payments', f);
      onGuardado();
    } catch (err) { onError(err.response?.data?.error || 'No se pudo registrar el pago'); }
    finally { setGuardando(false); }
  };

  return (
    <div className="modal-overlay" onClick={onCerrar}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Registrar pago</span>
          <button className="btn btn-ghost btn-icon" onClick={onCerrar}>✕</button>
        </div>
        <form onSubmit={guardar}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Negocio *</label>
              <select className="form-select" required value={f.negocio_id} onChange={e => elegirNegocio(e.target.value)}>
                <option value="">Seleccionar...</option>
                {negocios.map(n => <option key={n.id} value={n.id}>{n.nombre}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group">
                <label className="form-label">Monto *</label>
                <input type="number" className="form-input" required value={f.monto}
                  onChange={e => setF(v => ({ ...v, monto: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Método</label>
                <select className="form-select" value={f.metodo} onChange={e => setF(v => ({ ...v, metodo: e.target.value }))}>
                  {metodos.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Estado</label>
                <select className="form-select" value={f.estado} onChange={e => setF(v => ({ ...v, estado: e.target.value }))}>
                  <option value="recibido">Recibido</option>
                  <option value="pendiente">Pendiente</option>
                  <option value="fallido">Fallido</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{f.estado === 'pendiente' ? 'Vencimiento' : 'Fecha de pago'}</label>
                <input type="date" className="form-input"
                  value={f.estado === 'pendiente' ? f.fecha_vencimiento : f.fecha_pago}
                  onChange={e => setF(v => f.estado === 'pendiente'
                    ? { ...v, fecha_vencimiento: e.target.value }
                    : { ...v, fecha_pago: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Referencia</label>
              <input className="form-input" placeholder="N° de transacción o comprobante"
                value={f.referencia} onChange={e => setF(v => ({ ...v, referencia: e.target.value }))} />
            </div>
            {f.estado === 'fallido' && (
              <div className="form-group">
                <label className="form-label">Motivo del fallo</label>
                <input className="form-input" value={f.motivo_fallo || ''}
                  onChange={e => setF(v => ({ ...v, motivo_fallo: e.target.value }))} />
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onCerrar}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={guardando}>
              {guardando ? 'Guardando...' : 'Registrar pago'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModalReembolso({ negocios, onCerrar, onGuardado, onError }) {
  const [f, setF] = useState({ negocio_id: '', monto: '', motivo: '', fecha: hoy(), estado: 'completado' });
  const [guardando, setGuardando] = useState(false);

  const guardar = async e => {
    e.preventDefault();
    setGuardando(true);
    try {
      await api.post('/payments/refunds', f);
      onGuardado();
    } catch (err) { onError(err.response?.data?.error || 'No se pudo registrar el reembolso'); }
    finally { setGuardando(false); }
  };

  return (
    <div className="modal-overlay" onClick={onCerrar}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Registrar reembolso</span>
          <button className="btn btn-ghost btn-icon" onClick={onCerrar}>✕</button>
        </div>
        <form onSubmit={guardar}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Negocio</label>
              <select className="form-select" value={f.negocio_id} onChange={e => setF(v => ({ ...v, negocio_id: e.target.value }))}>
                <option value="">Seleccionar...</option>
                {negocios.map(n => <option key={n.id} value={n.id}>{n.nombre}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Monto *</label>
              <input type="number" className="form-input" required value={f.monto}
                onChange={e => setF(v => ({ ...v, monto: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Motivo</label>
              <input className="form-input" placeholder="Solicitud del cliente, error en cobro..."
                value={f.motivo} onChange={e => setF(v => ({ ...v, motivo: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Fecha</label>
              <input type="date" className="form-input" value={f.fecha}
                onChange={e => setF(v => ({ ...v, fecha: e.target.value }))} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onCerrar}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={guardando}>
              {guardando ? 'Guardando...' : 'Registrar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
